/**
 * Error Boundary Komponente
 * 
 * Fängt JavaScript-Fehler in der Komponenten-Hierarchie ab
 * und zeigt einen Fallback-UI statt des gecrashten Baums.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { captureException } from '../../utils/sentry';

interface Props {
  children: ReactNode;
  /** Optionaler Fallback-UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Send to Sentry (Production + Development)
    captureException(error);

    // Log to Console (Development only)
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      // Custom Fallback wenn übergeben
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Standard Fallback-UI
      return (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card border-danger">
                <div className="card-header bg-danger text-white">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  Etwas ist schiefgelaufen
                </div>
                <div className="card-body">
                  <h5 className="card-title">Ein unerwarteter Fehler ist aufgetreten</h5>
                  <p className="card-text text-muted">
                    {this.state.error?.message || 'Unbekannter Fehler'}
                  </p>
                  
                  {/* Debug-Info nur in Development */}
                  {import.meta.env.DEV && this.state.errorInfo && (
                    <details className="mt-3">
                      <summary className="text-muted" style={{ cursor: 'pointer' }}>
                        Technische Details
                      </summary>
                      <pre className="mt-2 p-3 bg-light rounded small overflow-auto" style={{ maxHeight: '200px' }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}

                  <div className="mt-4">
                    <button
                      className="btn btn-outline-secondary me-2"
                      onClick={this.handleRetry}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Erneut versuchen
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={this.handleReload}
                    >
                      <i className="bi bi-arrow-repeat me-1"></i>
                      Seite neu laden
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

